import { useEffect, useMemo, useState } from "react";
import { Button, Input, InputNumber, Modal, Popover, Select, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import DeleteIcon from "@mui/icons-material/Delete";
import { TableCell } from "@mui/material";
import TabBox from "src/Components/Tabbox/TabBox";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
import { IAdditional, IAdditionalHeaderProps } from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import { ITableReport, Report } from "src/Interfaces/TableInterfaces";
import {
  cancelBaseExecutionOrder, deleteBaseExecutionPredefinedProgram, BaseExecutionDetailSearch,
  BaseExecutionErrorRow, BaseExecutionOrderRow, BaseExecutionPredefinedProgram,
  BaseExecutionProgram, BaseExecutionProgramRow, BaseExecutionTaskRow,
  BaseExecutionTradeRow, getBaseExecutionErrors, getBaseExecutionOrders,
  getBaseExecutionPredefinedPrograms, getBaseExecutionProgram,
  getBaseExecutionTrades, saveBaseExecutionPredefinedProgram,
  saveBaseExecutionProgram,
} from "src/Requests/baseMarketMaking";

type Side = "buy" | "sell";
const OrderActionsHeader = ({ selectedRowsIds, setSelectedRowsIds, onAction }: IAdditionalHeaderProps) => {
  const [busy, setBusy] = useState(false);
  const cancelSelected = () => {
    const ids = [...selectedRowsIds];
    if (!ids.length || busy) return;
    Modal.confirm({
      title: `لغو ${ids.length} سفارش انتخاب‌شده؟`,
      okText: "لغو سفارش‌ها", cancelText: "انصراف", okButtonProps: { danger: true },
      onOk: async () => {
        setBusy(true);
        const failed: number[] = [];
        try {
          for (const id of ids) {
            try { await cancelBaseExecutionOrder(id); }
            catch { failed.push(id); }
          }
          const cancelled = ids.length - failed.length;
          if (cancelled) {
            message.success(`لغو ${cancelled} سفارش تأیید شد`);
            onAction();
          }
          if (failed.length) message.warning(`لغو ${failed.length} سفارش تأیید نشد`);
          setSelectedRowsIds(failed);
        } finally { setBusy(false); }
      },
    });
  };
  return <TableCell>
    <div className="flex items-center gap-1">
      <button type="button" aria-label="لغو گروهی سفارش‌ها" title="لغو سفارش‌های انتخاب‌شده"
        disabled={!selectedRowsIds.length || busy}
        className="rounded p-1 text-red-700 transition-all hover:bg-red-100 disabled:cursor-not-allowed disabled:text-gray-300"
        onClick={cancelSelected}><DeleteIcon fontSize="small" /></button>
    </div>
  </TableCell>;
};
const OrderActions: IAdditional<BaseExecutionOrderRow> = {
  getHeader: OrderActionsHeader,
  getRow: ({ row, onAction }) => <TableCell>
    {row.isActive && <button type="button" aria-label="لغو سفارش" title="لغو سفارش"
      className="rounded p-1 transition-all hover:bg-red-100 text-red-700"
      onClick={event => {
        event.stopPropagation();
        void cancelBaseExecutionOrder(row.id)
          .then(() => { message.success("درخواست لغو سفارش ارسال شد"); onAction(); })
          .catch(() => message.warning("لغو سفارش تأیید نشد"));
      }}><DeleteIcon fontSize="small" /></button>}
  </TableCell>,
};
type DraftRow = {
  rowNo: number;
  from: number | null;
  to: number | null;
  flexibilityAmount: number | null;
  flexibilityPercent: number | null;
};
type DraftProgram = { buy: DraftRow[]; sell: DraftRow[] };

const emptyRows = (): DraftRow[] => Array.from({ length: 5 }, (_, index) => ({
  rowNo: index + 1, from: null, to: null, flexibilityAmount: null, flexibilityPercent: null,
}));
const emptyProgram = (): DraftProgram => ({ buy: emptyRows(), sell: emptyRows() });
const toDraft = (program: BaseExecutionProgram): DraftProgram => ({
  buy: emptyRows().map(row => ({ ...row, ...program.buy.find(x => x.rowNo === row.rowNo) })),
  sell: emptyRows().map(row => ({ ...row, ...program.sell.find(x => x.rowNo === row.rowNo) })),
});

const withSymbols = <Row extends ITableReport & { instrumentId: string }>(
  reports: Report<Row>, symbols: Record<string, string>,
): Report<Row> => ({
  ...reports,
  report: reports.report.map(row => ({
    ...row,
    instrumentId: symbols[row.instrumentId] || row.instrumentId,
  })),
});

const toPayload = (draft: DraftProgram): BaseExecutionProgram | null => {
  const convert = (rows: DraftRow[]): BaseExecutionProgramRow[] | null => {
    const filled = rows.filter(row => [row.from, row.to, row.flexibilityAmount,
      row.flexibilityPercent].some(value => value !== null));
    if (!filled.length || filled.some(row => row.from === null || row.to === null ||
      row.flexibilityAmount === null || row.flexibilityPercent === null || row.to < row.from))
      return null;
    return filled.map(row => ({ rowNo: row.rowNo, from: row.from!, to: row.to!,
      flexibilityAmount: row.flexibilityAmount!, flexibilityPercent: row.flexibilityPercent! }));
  };
  const buy = convert(draft.buy);
  const sell = convert(draft.sell);
  return buy && sell ? { buy, sell } : null;
};

interface Props {
  task: BaseExecutionTaskRow | null;
  symbols: Record<string, string>;
  onClearSelection: () => void;
}

const ExecutionDetails = ({ task, symbols, onClearSelection }: Props) => {
  const [tab, setTab] = useState(task ? 0 : 1);
  const [program, setProgram] = useState<DraftProgram>(emptyProgram);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [predefined, setPredefined] = useState<BaseExecutionPredefinedProgram[]>([]);
  const [selectedPredefined, setSelectedPredefined] = useState<number>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [predefinedOpen, setPredefinedOpen] = useState(false);
  const [predefinedName, setPredefinedName] = useState("");
  const [errorCount, setErrorCount] = useState(0);
  const search = useMemo<BaseExecutionDetailSearch>(() =>
    task ? { taskId: task.id } : {}, [task?.id]);
  const allSearch = useMemo<BaseExecutionDetailSearch>(() => ({}), []);
  const canSaveProgram = toPayload(program) !== null;

  const refreshPredefined = () => getBaseExecutionPredefinedPrograms()
    .then(setPredefined).catch(() => message.error("دریافت پیش‌فرض‌ها ناموفق بود"));

  useEffect(() => { if (task) refreshPredefined(); }, [task?.id]);
  useEffect(() => {
    let current = true;
    setProgram(emptyProgram());
    setSelectedPredefined(undefined);
    setDeleteConfirmOpen(false);
    if (!task) {
      setTab(current => current === 0 ? 1 : current);
      setLoading(false);
      return;
    }
    setLoading(true);
    getBaseExecutionProgram(task.id)
      .then(value => { if (current) setProgram(toDraft(value)); })
      .catch(() => { if (current) message.error("دریافت تنظیمات ارسال ناموفق بود"); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [task?.id]);

  useEffect(() => {
    let current = true;
    const loadCount = () => getBaseExecutionErrors(allSearch, 0, 1)
      .then(result => { if (current) setErrorCount(result.count ?? 0); })
      .catch(() => { if (current) setErrorCount(0); });
    loadCount();
    const timer = setInterval(loadCount, 10000);
    return () => { current = false; clearInterval(timer); };
  }, [allSearch]);

  const updateRow = (side: Side, index: number, key: keyof Omit<DraftRow, "rowNo">,
    value: number | null) => setProgram(current => ({
      ...current,
      [side]: current[side].map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row),
    }));

  const validProgram = () => {
    const payload = toPayload(program);
    if (!payload) message.warning("برای خرید و فروش حداقل یک ردیف کامل و معتبر وارد کنید");
    return payload;
  };

  const save = async () => {
    if (!task) return;
    const payload = validProgram();
    if (!payload) return;
    setSaving(true);
    try {
      setProgram(toDraft(await saveBaseExecutionProgram(task.id, payload)));
      message.success("تنظیمات ارسال ثبت شد");
    } catch { message.error("ثبت تنظیمات ارسال ناموفق بود"); }
    finally { setSaving(false); }
  };

  const savePredefined = async () => {
    const payload = validProgram();
    if (!payload) return;
    if (!predefinedName.trim()) return message.warning("نام پیش‌فرض را وارد کنید");
    setSaving(true);
    try {
      const saved = await saveBaseExecutionPredefinedProgram({
        name: predefinedName.trim(), ...payload,
      });
      setSelectedPredefined(saved.id);
      setPredefinedOpen(false);
      setPredefinedName("");
      await refreshPredefined();
      message.success("پیش‌فرض ثبت شد");
    } catch { message.error("ثبت پیش‌فرض ناموفق بود"); }
    finally { setSaving(false); }
  };

  const deletePredefined = async () => {
    if (!selectedPredefined) return;
    try {
      await deleteBaseExecutionPredefinedProgram(selectedPredefined);
      setDeleteConfirmOpen(false);
      setSelectedPredefined(undefined);
      await refreshPredefined();
      message.success("پیش‌فرض حذف شد");
    } catch { message.error("حذف پیش‌فرض ناموفق بود"); }
  };

  const programTable = (side: Side, compact = false) => <div className={compact ? "w-full min-w-0" : "w-[450px] max-w-full min-w-0"}>
    <table className="w-full min-w-0 table-fixed border-collapse text-center text-xs">
      <thead><tr>
        <th className="w-9 border p-1 align-middle">ردیف</th>
        <th className="h-12 border px-1 py-2 align-middle text-[11px] leading-4 !whitespace-normal break-words">
          <span className="block">حجم {side === "buy" ? "خرید" : "فروش"}</span><span className="block">ابتدای بازه</span>
        </th>
        <th className="h-12 border px-1 py-2 align-middle text-[11px] leading-4 !whitespace-normal break-words">
          <span className="block">حجم {side === "buy" ? "خرید" : "فروش"}</span><span className="block">انتهای بازه</span>
        </th>
        <th className="h-12 border px-1 py-2 align-middle text-[11px] leading-4 !whitespace-normal break-words">
          <span className="block">قیمت {side === "buy" ? "خرید" : "فروش"}</span><span className="block">مقدار انعطاف</span>
        </th>
        <th className="h-12 border px-1 py-2 align-middle text-[11px] leading-4 !whitespace-normal break-words">
          <span className="block">قیمت {side === "buy" ? "خرید" : "فروش"}</span><span className="block">درصد انعطاف</span>
        </th>
      </tr></thead>
      <tbody>{program[side].map((row, index) => <tr key={row.rowNo}>
        <td className="border p-1">{row.rowNo}</td>
        {(["from", "to", "flexibilityAmount", "flexibilityPercent"] as const).map(key =>
          <td className="min-w-0 border p-1" key={key}><InputNumber min={0} className="!w-full !min-w-0"
            precision={key === "from" || key === "to" ? 0 : undefined}
            value={row[key]} onChange={value => updateRow(side, index, key, value)} /></td>)}
      </tr>)}</tbody>
    </table>
  </div>;

  return <section className="w-full border border-gray-300 rounded p-3 flex flex-col gap-4" dir="rtl">
    <div className="flex items-center gap-3">
      <div className="w-fit"><TabBox activeTab={tab} onChange={(_, value) => setTab(value)} children={[
        ...(task ? [{ index: 0, title: "تنظیمات ارسال" }] : []),
        { index: 1, title: "سفارشات" },
        { index: 2, title: "معاملات" },
        { index: 3, title: "خطا", count: errorCount },
      ]} /></div>
      {task && <Button size="small" onClick={onClearSelection}>نمایش همه</Button>}
    </div>

    {task && tab === 0 && <div className="flex flex-col gap-3">
      <div className="mx-auto flex w-full max-w-[1350px] flex-wrap items-start justify-between gap-6"
        style={{ pointerEvents: loading ? "none" : undefined, opacity: loading ? 0.5 : 1 }}>
        {programTable("buy")}{programTable("sell")}
      </div>
      <div className="flex items-center gap-2 mr-auto">
        <Select placeholder="پیش فرض" className="min-w-[140px]" allowClear
          value={selectedPredefined} onChange={value => {
            setDeleteConfirmOpen(false);
            setSelectedPredefined(value);
            const selected = predefined.find(item => item.id === value);
            if (selected) setProgram(toDraft(selected));
          }} options={predefined.map(item => ({ value: item.id, label: item.name }))} />
        {selectedPredefined && <Popover trigger="click" placement="top"
          open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}
          content={<div className="w-[190px]" dir="rtl">
            <div className="mb-3 whitespace-nowrap">پیش‌فرض حذف شود؟</div>
            <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap">
              <Button size="small" type="primary" danger onClick={deletePredefined}>حذف</Button>
              <Button size="small" onClick={() => setDeleteConfirmOpen(false)}>انصراف</Button>
            </div>
          </div>}>
          <Button danger icon={<DeleteOutlined />} />
        </Popover>}
        <Button disabled={loading || !canSaveProgram}
          onClick={() => setPredefinedOpen(true)}>ثبت پیشفرض</Button>
        <Button type="primary" loading={saving} onClick={save}>ثبت</Button>
      </div>
    </div>}

    {tab === 1 && <TableTemplate<BaseExecutionDetailSearch, BaseExecutionOrderRow>
      refreshTableTime={5000} withSort getDataTable={getBaseExecutionOrders}
      searchParameters={search} TableUI={({ reports, onAction, sortingType, setSortingType }) =>
        <CommonTableUI key={task?.id ?? "all"} reports={withSymbols(reports, symbols)} onAction={onAction}
          AdditionalBody={OrderActions} withSelect isRowSelectable={row => row.isActive} sortingType={sortingType}
          setSortingType={setSortingType} tableBoxClassName="max-h-[300px]" />} />}
    {tab === 2 && <TableTemplate<BaseExecutionDetailSearch, BaseExecutionTradeRow>
      refreshTableTime={5000} withSort getDataTable={getBaseExecutionTrades}
      searchParameters={search} TableUI={({ reports, onAction, sortingType, setSortingType }) =>
        <CommonTableUI reports={withSymbols(reports, symbols)} onAction={onAction} sortingType={sortingType}
          setSortingType={setSortingType} tableBoxClassName="max-h-[300px]" />} />}
    {tab === 3 && <TableTemplate<BaseExecutionDetailSearch, BaseExecutionErrorRow>
      refreshTableTime={10000} withSort getDataTable={getBaseExecutionErrors}
      searchParameters={allSearch} TableUI={({ reports, onAction, sortingType, setSortingType }) =>
        <CommonTableUI reports={withSymbols(reports, symbols)} onAction={onAction} sortingType={sortingType}
          setSortingType={setSortingType} tableBoxClassName="max-h-[300px]" />} />}
    <Modal open={predefinedOpen} width="min(1000px, calc(100vw - 32px))" footer={null}
      onCancel={() => { setPredefinedOpen(false); setPredefinedName(""); }}>
      <div className="flex min-w-0 flex-col gap-5 pt-5" dir="rtl">
        <Input placeholder="نام پیش‌فرض" className="!w-44"
          value={predefinedName} onChange={event => setPredefinedName(event.target.value)} />
        <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-2">
          {programTable("buy", true)}
          {programTable("sell", true)}
        </div>
        <Button type="primary" block loading={saving} disabled={!canSaveProgram}
          onClick={savePredefined}>ثبت</Button>
      </div>
    </Modal>
  </section>;
};

export default ExecutionDetails;
